import React from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { updateCustomerInfo } from '../../../../../store/slices/createCRSlice';
import { DateInput, FormInput } from '../../../../../components/shared/inputs';

interface CustomerInfoFieldsProps {
  disabled?: boolean;
}

export const CustomerInfoFields: React.FC<CustomerInfoFieldsProps> = ({ disabled = false }) => {
  const dispatch = useAppDispatch();
  const customerInfo = useAppSelector(state => state.createCR.form_data.task.customer_info);

  const handleFieldChange = (field: string, value: string) => {
    dispatch(updateCustomerInfo({ [field]: value }));
  };

  const isTexasState = customerInfo.state_short_name.toLowerCase() === 'texas' ||
                     customerInfo.state_short_name.toLowerCase() === 'tx';

  return (
    <div className={`relative px-5 py-4 3xl:px-6 3xl:py-5 space-y-2 3xl:space-y-2.5 transition-opacity duration-200 ${disabled ? 'pointer-events-none opacity-40 select-none' : ''}`}>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
        <div className="px-5 py-3 3xl:px-6 3xl:py-3.5 bg-orange-100/50 border-b border-slate-200/60">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-orange-500 self-center" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <div>
              <span className="block text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Customer Information</span>
              <p className="text-[10px] 3xl:text-xs text-slate-400 mt-0.5">Address, project details, and utility info</p>
            </div>
          </div>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-slate-200/70">
            {/* Customer Details */}
            <div className="pb-4 lg:pb-0 lg:pr-5 space-y-3">
              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
                </svg>
                <span className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-bold text-slate-600">Customer Details</span>
              </div>
              <div className="space-y-3 3xl:space-y-3.5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormInput
                    label="Street Name"
                    value={customerInfo.street_name}
                    onChange={(value) => handleFieldChange('street_name', value)}
                  />
                  <FormInput
                    label="Street Number"
                    value={customerInfo.street_number}
                    onChange={(value) => handleFieldChange('street_number', value)}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormInput
                    label="City"
                    value={customerInfo.city}
                    onChange={(value) => handleFieldChange('city', value)}
                  />
                  <FormInput
                    label="Zip Code"
                    value={customerInfo.zip_code}
                    onChange={(value) => handleFieldChange('zip_code', value)}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormInput
                    label="State"
                    value={customerInfo.state_short_name}
                    onChange={(value) => handleFieldChange('state_short_name', value)}
                    placeholder="State"
                  />
                  <div className="relative group">
                    <FormInput
                      label="Texas ESI ID"
                      value={customerInfo.texas_esi_id}
                      onChange={(value) => handleFieldChange('texas_esi_id', value)}
                      placeholder="Enter ESI ID"
                      disabled={!isTexasState}
                      inputClassName={!isTexasState ? 'bg-slate-50 text-slate-400' : ''}
                      tooltip="Enabled when state is TX"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="pt-4 lg:pt-0 lg:pl-5 space-y-3">
              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-bold text-slate-600">Additional Info</span>
              </div>
              <div className="space-y-3 3xl:space-y-3.5">
                <DateInput
                  label="Design Date"
                  value={customerInfo.design_date}
                  onChange={(value) => handleFieldChange('design_date', value)}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormInput
                    label="Utility Name"
                    value={customerInfo.utility_name}
                    onChange={(value) => handleFieldChange('utility_name', value)}
                  />
                  <FormInput
                    label="Utility Meter Number (UM#)"
                    value={customerInfo.utility_meter_number}
                    onChange={(value) => handleFieldChange('utility_meter_number', value)}
                    placeholder="Enter meter number"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormInput
                    label="AHJ Name"
                    value={customerInfo.ahj_name}
                    onChange={(value) => handleFieldChange('ahj_name', value)}
                  />
                  <FormInput
                    label="Parcel Number (APN)"
                    value={customerInfo.parcel_number_apn}
                    onChange={(value) => handleFieldChange('parcel_number_apn', value)}
                    placeholder="Enter APN"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Note banner */}
      {/* <div className="flex items-center gap-1.5 px-1">
        <svg className="w-3 h-3 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.832c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <p className="text-[10px] 3xl:text-[11px] text-slate-400">
          <span className="font-medium text-amber-600">Note:</span> Data comes from Asana and may be inaccurate. Please review before continuing.
        </p>
      </div> */}
    </div>
  );
};
